/**
 * Anonymous player / game-session tracking.
 *
 * Writes a lightweight row per game session to Supabase (`game_sessions`) so we can
 * report daily unique players, completions, wins, losses and abandoned games.
 * Key moments are mirrored to Google Analytics.
 *
 * No personal data is stored — only a random anonymous id kept in localStorage.
 */
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";

const PLAYER_ID_KEY = "babblegram_player_id";
const SESSION_KEY_PREFIX = "babblegram_session_id_";

type Mode = "daily" | "practice" | "custom";

const isBrowser = () => typeof window !== "undefined";

const newId = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
};

export const getPlayerId = (): string => {
  if (!isBrowser()) return "server";
  try {
    let id = localStorage.getItem(PLAYER_ID_KEY);
    if (!id) {
      id = newId();
      localStorage.setItem(PLAYER_ID_KEY, id);
    }
    return id;
  } catch {
    return "unknown";
  }
};

const todayKey = () => new Date().toISOString().slice(0, 10);
const sessionStorageKey = (mode: Mode) => `${SESSION_KEY_PREFIX}${mode}_${todayKey()}`;

const getStoredSessionId = (mode: Mode): string | null => {
  if (!isBrowser()) return null;
  try {
    return localStorage.getItem(sessionStorageKey(mode));
  } catch {
    return null;
  }
};

const setStoredSessionId = (mode: Mode, id: string | null) => {
  if (!isBrowser()) return;
  try {
    if (id) localStorage.setItem(sessionStorageKey(mode), id);
    else localStorage.removeItem(sessionStorageKey(mode));
  } catch {
    /* ignore */
  }
};

/**
 * Records the start of a game session. De-duplicated per mode per day on the client,
 * so returning to a game in progress does not create a second row.
 */
export const startGameSession = async (mode: Mode = "daily"): Promise<string | null> => {
  if (!isBrowser()) return null;

  const existing = getStoredSessionId(mode);
  if (existing) return existing;

  const id = newId();
  const player_id = getPlayerId();
  const game_date = todayKey();

  // Store first so rapid re-entry cannot double-insert.
  setStoredSessionId(mode, id);
  trackEvent("game_start", { mode, game_date });

  try {
    const { error } = await supabase.from("game_sessions").insert({
      id,
      player_id,
      game_date,
      mode,
      outcome: "started",
    });
    if (error) throw error;
  } catch (error) {
    console.warn("[playerTracking] Failed to start session", error);
  }

  return id;
};

/** Updates the highest level a player reached in the current session. */
export const trackLevelReached = async (mode: Mode, level: number) => {
  const id = getStoredSessionId(mode);
  if (!id) return;
  try {
    await supabase.from("game_sessions").update({ max_level: level }).eq("id", id);
  } catch (error) {
    console.warn("[playerTracking] Failed to update level", error);
  }
};

/** Marks the current session as finished (won or lost). */
export const completeGameSession = async (
  mode: Mode,
  outcome: "won" | "lost",
  details: { maxLevel?: number; score?: number } = {}
) => {
  let id = getStoredSessionId(mode);
  if (!id) id = await startGameSession(mode);
  if (!id) return;

  try {
    const { error } = await supabase
      .from("game_sessions")
      .update({
        outcome,
        completed_at: new Date().toISOString(),
        ...(details.maxLevel ? { max_level: details.maxLevel } : {}),
        ...(details.score !== undefined ? { score: details.score } : {}),
      })
      .eq("id", id);
    if (error) throw error;
  } catch (error) {
    console.warn("[playerTracking] Failed to complete session", error);
  }
};

/** Clears the local session pointer so a new session row is created (e.g. Play Again). */
export const resetGameSession = (mode: Mode) => setStoredSessionId(mode, null);
